import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './category-selector.css';

export function CategoryListSelector({
    categories,
    onCategorySelected = () => {},
    categoriesSelected = [],
}) {
    const [activeCategories, setActiveCategories] =
    React.useState(categoriesSelected);

    React.useEffect(() => {
        if (
            activeCategories === categoriesSelected ||
      (activeCategories.length === 0 && categoriesSelected.length === 0) ||
      (activeCategories.length === categories.length &&
        categoriesSelected.length === categories.length)
        ) {
            return;
        }
        setActiveCategories(categoriesSelected);
    }, [categoriesSelected]);

    const onCategoryClicked = React.useCallback(
        (category) => {
            //Compute active categories and notify host app about a clic
            let tmpActiveCategories = [...activeCategories];
            let indexCategory = tmpActiveCategories.findIndex(
                (category_uuid) => category_uuid === category.uuid,
            );
            if (indexCategory > -1) {
                //Remove category from array
                tmpActiveCategories.splice(indexCategory, 1);
            } else {
                //Add Category to array
                tmpActiveCategories.push(category.uuid);
            }
            //If every categories available are seletected then it's like none of them are
            let categoriesFiltered = categories.filter((category) =>
                category.rates.some((rate) => rate.available_size > 0),
            );
            if (categoriesFiltered.length === tmpActiveCategories.length) {
                setActiveCategories([]);
                onCategorySelected([]);
            } else {
                setActiveCategories([...tmpActiveCategories]);
                onCategorySelected(tmpActiveCategories);
            }
        },
        [activeCategories, setActiveCategories, onCategorySelected, categories],
    );

    return (
        <div className="categories-list-selector">
            <div className="categories-list-subcontainer">
                {categories.map((category) => (
                    <CategoryItem
                        key={category.uuid}
                        category={category}
                        onCategoryClicked={onCategoryClicked}
                        active={
                            activeCategories.length
                                ? activeCategories.some(
                                    (category_uuid) => category_uuid === category.uuid,
                                )
                                : true
                        }
                        showRatesRange={true}
                    />
                ))}
            </div>
        </div>
    );
}

CategoryListSelector.propTypes = {
    categories: PropTypes.array.isRequired,
    onCategorySelected: PropTypes.func.isRequired,
    categoriesSelected: PropTypes.array,
};

export function CategoryItem({
    category,
    onCategoryClicked = () => {},
    active,
    showRatesRange = false,
}) {
    const { t } = useTranslation();
    const available = React.useRef(true);

    const ratesRange = React.useMemo(() => {
        if (!showRatesRange) {
            return;
        }

        //we are looking for min and max value
        let min = category.rates.reduce((prev, curr) =>
            prev.price < curr.price ? prev : curr,
        );
        let max = category.rates.reduce((prev, curr) =>
            prev.price > curr.price ? prev : curr,
        );
        //Check if at least one rate is available
        available.current = category.rates.some((rate) => rate.available_size > 0);

        //Only one rate available ?
        if (min.price === max.price) {
            return t('uniqueRateCategory', { rate: min.priceText });
        }

        return t('fromRateCategory', { min: min.priceText, max: max.priceText });
    }, [category, showRatesRange, t]);

    return (
        <div className="category-item">
            <ButtonItem
                title={
                    available.current
                        ? category.name
                        : `${category.name} - ${t('unavailable')}`
                }
                subtitle={ratesRange}
                //If there isn't any categories selected => every rates are active, otherwise only selected rates are
                active={active}
                available={available.current}
                onClick={() => onCategoryClicked(category)}
                style={category.style || null}
                color={available.current ? category.color : '#9A9A9A'}
            />
        </div>
    );
}

CategoryItem.propTypes = {
    category: PropTypes.object.isRequired,
    onCategoryClicked: PropTypes.func,
    active: PropTypes.bool,
    showRatesRange: PropTypes.bool,
};

export function ButtonItem({
    title,
    subtitle = null,
    active = false,
    onClick = () => {},
    available = true,
    style = {},
    color = '#9A9A9A',
}) {
    let buttonStyle;
    if (style) {
        buttonStyle = { ...style };
    } else {
        buttonStyle =
      available && active
          ? {
              backgroundColor: color,
              color: 'white',
              border: `1px solid ${color}`,
          }
          : {
              backgroundColor: 'transparent',
              border: `1px solid ${color}`,
              color: color,
          };
    }

    return (
        <div
            className={`button-item ${available ? (active ? 'active' : 'inactive') : 'unavailable'}`}
            onClick={available ? onClick : () => {}}
            style={buttonStyle}
        >
            <div className="button-item-title">{title}</div>
            {subtitle && <div className="button-item-subtitle">{subtitle}</div>}
        </div>
    );
}

ButtonItem.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    active: PropTypes.bool,
    available: PropTypes.bool,
    onClick: PropTypes.func,
    style: PropTypes.object,
    color: PropTypes.string,
};
